/**
 * ClarityRouteTracker - Microsoft Clarity SPA Route Tracking
 * 
 * Ensures Clarity properly tracks page transitions in React SPA.
 * Without this, Clarity may not detect route changes and session
 * recordings may appear incomplete.
 * 
 * Usage: Mount once inside BrowserRouter, before Routes
 */
import { useLocation } from 'react-router-dom';
import { useEffect, useRef } from 'react';

const ClarityRouteTracker = () => {
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);

  useEffect(() => {
    // Skip if same path (prevents double-firing on initial mount)
    if (prevPathRef.current === location.pathname && 
        prevPathRef.current !== '/') {
      return;
    }
    
    prevPathRef.current = location.pathname;

    // Check if Clarity is loaded
    if (typeof window.clarity === 'function') {
      try {
        // Set the current page in Clarity
        const fullPath = location.pathname + location.search;
        window.clarity('set', 'page', fullPath);
        
        // Fire a custom event for route change
        window.clarity('event', 'route_change');
        
        // Log for debugging (can be removed in production)
        if (process.env.NODE_ENV === 'development' || 
            window.location.search.includes('debugClarity=1')) {
          console.log('[Clarity] Route tracked:', fullPath);
        }
      } catch (error) {
        console.warn('[Clarity] Failed to track route:', error);
      }
    }
  }, [location.pathname, location.search]);

  // This component doesn't render anything
  return null;
};

export default ClarityRouteTracker;
